import { useGlobalAdmin } from '@/hooks/use-global-admin';
import { useTheme } from '@/hooks/use-theme';
import { FEATURE_FLAGS, useFeatureFlag } from '@/lib/feature-flags';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from '@beacon/ui/components';
import { useNavigate } from 'react-router';

export function SidebarUserMenu({ collapsed }: { collapsed?: boolean }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth0();
  const { isGlobalAdmin } = useGlobalAdmin();
  const isBeaconStaff = useFeatureFlag(FEATURE_FLAGS.IS_BEACON_STAFF);
  const { theme, toggle: toggleTheme } = useTheme();

  const initial = user?.name?.[0] ?? user?.email?.[0]?.toUpperCase() ?? '?';
  const displayName = user?.name ?? user?.email ?? 'User';

  const avatar = (
    <Avatar size="sm" className="shrink-0">
      {user?.picture ? (
        <img src={user.picture} alt={displayName} className="h-full w-full rounded-full object-cover" />
      ) : (
        <span className="bg-primary-9 flex h-full w-full items-center justify-center text-xs font-medium text-white">
          {initial}
        </span>
      )}
    </Avatar>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {collapsed ? (
          <button
            aria-label={displayName}
            className="hover:bg-surface-2 flex w-full justify-center rounded py-1.5 transition-colors"
          >
            {avatar}
          </button>
        ) : (
          <button className="hover:bg-surface-2 flex w-full items-center gap-2.5 rounded px-2.5 py-2 transition-colors">
            {avatar}
            <span className="text-text-1 min-w-0 flex-1 truncate text-left text-xs font-medium">{displayName}</span>
            <Icon symbol="ri-more-2-fill" className="text-text-3 h-3.5 w-3.5 shrink-0" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent side={collapsed ? 'right' : 'top'} align={collapsed ? 'end' : 'start'} className="w-52">
        {isBeaconStaff && (
          <>
            {isGlobalAdmin && <DropdownMenuItem onSelect={() => navigate('/admin')}>Admin Settings</DropdownMenuItem>}
            <DropdownMenuItem onSelect={() => navigate('/settings')}>Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onSelect={toggleTheme}>
          <span className="flex items-center gap-2">
            <Icon symbol={theme === 'dark' ? 'sun-line' : 'moon-line'} className="h-4 w-4" />
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
